import React, { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
    getAlertOptions,
    getAlertOpen,
} from '@atredux/selectors/atAlertSelector'

import Modal from '@amzn/meridian/modal'
import Row from '@amzn/meridian/row'
import Text from '@amzn/meridian/text'
import Button from '@amzn/meridian/button'
import Divider from '@amzn/meridian/divider'
import Column from '@amzn/meridian/column'

import { resetAlert, IAlertOptionsType } from '@atredux/slices/atAlertSlice'
import { CrossFrameUtils } from '@amzn/jwo-store-management-platform-components'
import { useBundle } from '@amzn/react-arb-tools'
import { BUNDLE_NAME } from '@src/constants'
import { t } from '@src/translationHelper'

interface IAlertOptionButtonsProps {
    closeAlert: () => void
    alertOptions: IAlertOptionsType
}

const yesCallback = (target) => {
    CrossFrameUtils.sendMessageToOtherFrame({
        frame: target.source,
        message: {
            method: 'alertYesCallback',
        },
    })
}

const noCallback = (target) => {
    CrossFrameUtils.sendMessageToOtherFrame({
        frame: target.source,
        message: {
            method: 'alertNoCallback',
        },
    })
}

const AlertOptionButtons = (
    props: IAlertOptionButtonsProps,
): React.ReactElement => {
    const [bundle] = useBundle(BUNDLE_NAME)
    const { closeAlert, alertOptions } = props
    const boundCallback = (cb: (target) => void) => {
        return () => {
            cb(alertOptions.target)
            closeAlert()
        }
    }

    return (
        <>
            {alertOptions.isYesNoAlert && (
                <Row alignmentHorizontal={'center'} alignmentVertical={'center'}>
                    <Button
                        type={'primary'}
                        minWidth={'100%'}
                        onClick={boundCallback(yesCallback)}
                    >
                        {alertOptions.yesText ? alertOptions.yesText : t(bundle, 'migrated-ihm-seaward-zone-timeout-yes-button')}
                    </Button>
                    <Button
                        type={'secondary'}
                        minWidth={'100%'}
                        onClick={boundCallback(noCallback)}
                    >
                        {alertOptions.noText ? alertOptions.noText : t(bundle, 'migrated-ihm-seaward-zone-timeout-no-button')}
                    </Button>
                </Row>
            )}
            {!alertOptions.isYesNoAlert && (
                <Row alignmentHorizontal={'center'} alignmentVertical={'center'}>
                    <Button
                        type={'tertiary'}
                        minWidth={'100%'}
                        onClick={closeAlert}
                    >
                        {alertOptions.okayText ? alertOptions.okayText : t(bundle, 'migrated-ihm-seaward-bluetooth-success-button')}
                    </Button>
                </Row>
            )}
        </>
    )
}

const AtAlertModal = (): React.ReactElement => {
    const [bundle] = useBundle(BUNDLE_NAME)
    const dispatch = useDispatch()
    const open = useSelector(getAlertOpen)
    const alertOptions: IAlertOptionsType = useSelector(getAlertOptions)
    const hasAnyCallbacks =
        alertOptions.noCallback ||
        alertOptions.yesCallback

    const closeAlert = useCallback(() => {
        dispatch(resetAlert())
    }, [dispatch])

    return (
        <Modal
            open={open}
            onClose={!hasAnyCallbacks && closeAlert}
            scrollContainer="viewport"
            closeLabel={t(bundle, 'jsma-web-common-close')}
            width="90vw"
        >
            <Column alignmentVertical={'center'}>
                <Row alignmentHorizontal={'center'} alignmentVertical={'center'}>
                    <Text>{alertOptions.message}</Text>
                </Row>
                <Divider size={'large'} />
                {AlertOptionButtons({ closeAlert, alertOptions })}
            </Column>
        </Modal>
    )
}

export default AtAlertModal
